export const getAnalysis = (score, config, testType) => {
  // Handle MDQ binary result
  if (testType === 'MDQ' && typeof score === 'object' && score.hasOwnProperty('isPositive')) {
    return {
      level: score.isPositive ? 'Positive Screen' : 'Negative Screen',
      desc: score.isPositive
        ? 'Meets criteria for Bipolar Spectrum Disorder screening. A comprehensive clinical evaluation is recommended.'
        : 'Does not meet screening criteria for Bipolar Spectrum Disorder based on MDQ alone.',
      isBinary: true,
      details: score
    };
  }

  // PC-PTSD-5 binary screen
  if (testType === 'PCPTSD5' && typeof score === 'object') {
    if (score.gateAnswer === 'No') {
      return {
        level: 'Negative Screen',
        desc: 'Patient denied exposure to potentially traumatic events. No further PTSD screening indicated at this time.',
        isBinary: true,
        details: score
      };
    }
    return {
      level: score.isPositive ? 'Positive Screen' : 'Negative Screen',
      desc: score.isPositive
        ? 'Score of ' + score.total + '/5 meets threshold (4+) for probable PTSD. A comprehensive clinical evaluation is recommended.'
        : 'Score of ' + score.total + '/5 does not meet threshold (4+) for probable PTSD.',
      isBinary: true,
      details: score
    };
  }

  // ASRS binary screener
  if (testType === 'ASRS' && typeof score === 'object') {
    return {
      level: score.isConsistent ? 'Consistent with ADHD' : 'Not Consistent with ADHD',
      desc: score.isConsistent
        ? 'Part A shaded count: ' + score.partAShadedCount + '/6 (threshold: 4+). Symptoms are highly consistent with ADHD in adults. Full clinical evaluation recommended.'
        : 'Part A shaded count: ' + score.partAShadedCount + '/6 (threshold: 4+). Symptoms are not highly consistent with ADHD based on Part A screener alone.',
      isBinary: true,
      details: score
    };
  }

  // PANSS (Kay, Fiszbein & Opler, 1987 - Positive and Negative Syndrome Scale).
  //
  // The primary source PDF (docs/official-scoring/sources/PANSS.pdf) defines
  // NO total-score interpretation bands. The five severity bands honored
  // here are SECONDARY, from Leucht et al. (2005) "What does the PANSS mean?"
  // (Schizophrenia Research 79:231-238), which equipercentile-linked
  // PANSS-Total to CGI-Severity anchors. Bands are declared in
  // src/tests/panss/config.json and looked up via config.ranges (NOT hard-
  // coded here, so config and analysis can never drift).
  //
  // The PANSS scale is 1-INDEXED for completed ratings (1 = Absent ...
  // 7 = Extreme). There is no "0 = not assessed" anchor on the PANSS
  // (unlike the BPRS). Minimum completed total is 30 (all 30 items at 1),
  // not 0; structural subscale minimums are 7/7/16. Maximum is 210 with
  // subscale maxima 49/49/112. The Composite Score (Positive - Negative)
  // ranges from -42 to +42.
  if (testType === 'PANSS' && typeof score === 'object' && config && Array.isArray(config.ranges)) {
    let band = config.ranges[0];
    for (const r of config.ranges) {
      if (score.total >= r.min && score.total <= r.max) { band = r; break; }
    }
    // Clamp out-of-range to nearest endpoint rather than silently defaulting.
    if (score.total < config.ranges[0].min) band = config.ranges[0];
    else if (score.total > config.ranges[config.ranges.length - 1].max) band = config.ranges[config.ranges.length - 1];
    return {
      level: band.label,
      desc:
        'Total: ' + score.total + '/210 (min 30). ' +
        'Positive: ' + score.positive + '/49 (min 7), ' +
        'Negative: ' + score.negative + '/49 (min 7), ' +
        'General: ' + score.general + '/112 (min 16). ' +
        'Composite (P - N): ' + score.composite + ' (range -42 to +42). ' +
        band.description + '. ' +
        'Bands: 30-58 Mildly ill, 59-75 Moderately ill, 76-95 Markedly ill, 96-116 Severely ill, 117-210 Among the most extremely ill. ' +
        'Bands are secondary (Leucht et al. 2005 PANSS-CGI linking, Schizophrenia Research 79:231-238); the primary PANSS source defines no cut-offs.',
      isBinary: false,
      details: score
    };
  }

  // CAPS-5 cluster result.
  // The official Past Week PDF defines NO severity bands; the bands below are
  // informational, sourced from older CAPS literature, and aligned with the
  // ranges declared in src/tests/caps5/config.json (0-11 / 12-22 / 23-34 /
  // 35-52 / 53-80). The PDF (p. 3 rule 7, p. 4 NOTE) states that the Past
  // Week version must NOT be used to establish PTSD diagnostic status; the
  // `criteriaItemMet` boolean below reflects ONLY the B/C/D/E item-count
  // rules and does not include criteria A, F, G, H, which require clinical
  // judgement.
  if (testType === 'CAPS5' && typeof score === 'object') {
    let severity = 'Asymptomatic/Few Symptoms';
    if (score.total >= 53) severity = 'Extreme PTSD';
    else if (score.total >= 35) severity = 'Severe PTSD';
    else if (score.total >= 23) severity = 'Threshold/Moderate PTSD';
    else if (score.total >= 12) severity = 'Subthreshold PTSD';
    const itemCounts = '[B>=1: ' + score.bMetCount + '/5, C>=1: ' + score.cMetCount + '/2, D>=2: ' + score.dMetCount + '/7, E>=2: ' + score.eMetCount + '/6]';
    const itemMet = score.criteriaItemMet ? 'B/C/D/E item-count rules MET' : 'B/C/D/E item-count rules NOT met';
    return {
      level: severity,
      desc: 'Total: ' + score.total + '/80 (B: ' + score.clusterB + ', C: ' + score.clusterC + ', D: ' + score.clusterD + ', E: ' + score.clusterE + ', Dissociation: ' + score.dissociation + '). ' + itemMet + ' ' + itemCounts + '. Past Week version is not for PTSD diagnostic status; criteria A, F, G, H require clinical judgement.',
      isBinary: false,
      details: score
    };
  }

  // PID-5 elevated domains
  if (testType === 'PID5' && typeof score === 'object' && score.domainScores) {
    const elevated = Object.entries(score.domainScores)
      .filter(([, avg]) => avg >= 2.0)
      .map(([name]) => name);
    return {
      level: elevated.length > 0 ? 'Elevated Domains Detected' : 'No Elevated Domains',
      desc: elevated.length > 0
        ? 'Elevated domains (average >= 2.0): ' + elevated.join(', ') + '.'
        : 'No personality trait domains reached the clinical elevation threshold (average >= 2.0).',
      isBinary: false,
      details: score
    };
  }

  // ISAA domain result.
  //
  // Official bands from Arya (2008) ISAA PDF, Table-11 "Norms of ISAA for
  // Diagnosis of Autism":
  //   < 70        Normal / No Autism
  //   70  - 106   Mild Autism       (70 to mean of autism-group = 106)
  //   107 - 153   Moderate Autism   (mean to mean + 2 SD = 153)
  //   > 153       Severe Autism     (> mean + 2 SD)
  //
  // The ISAA Likert scale is 1-indexed (1 = Rarely, 5 = Always) so the
  // theoretical minimum total is 40 (all 1s), NOT 0. A total below 40 is
  // structurally impossible and indicates a malformed answer vector.
  if (testType === 'ISAA' && typeof score === 'object' && score.domains) {
    let severity = 'No Autism';
    if (score.total >= 154) severity = 'Severe Autism';
    else if (score.total >= 107) severity = 'Moderate Autism';
    else if (score.total >= 70) severity = 'Mild Autism';
    const domainSummary = Object.entries(score.domains)
      .map(([name, val]) => name + ': ' + val)
      .join('; ');
    return {
      level: severity,
      desc: 'Total: ' + score.total + '/200 (min 40). Domains: ' + domainSummary + '. Bands: <70 No Autism, 70-106 Mild, 107-153 Moderate, >153 Severe.',
      isBinary: false,
      details: score
    };
  }

  // IGDS9-SF binary disorder flag
  if (testType === 'IGDS9SF' && typeof score === 'object') {
    return {
      level: score.isDisordered ? 'Internet Gaming Disorder Indicated' : 'No Internet Gaming Disorder',
      desc: 'Total: ' + score.total + '/45. Criteria met (max score items): ' + score.criteriaMetCount + '/9 (threshold: 5+).',
      isBinary: true,
      details: score
    };
  }

  // ASSIST per-substance risk
  if (testType === 'ASSIST' && typeof score === 'object' && score.substances) {
    const entries = Object.entries(score.substances);
    if (entries.length === 0) {
      return {
        level: 'No Substances Reported',
        desc: 'No substance use was reported.',
        isBinary: false,
        details: score
      };
    }
    const summary = entries.map(([name, s]) => name + ': ' + s.score + ' (' + s.risk + ')').join('; ');
    return {
      level: score.hasHighRisk ? 'High Risk Detected' : 'No High Risk',
      desc: summary,
      isBinary: false,
      details: score
    };
  }

  // CAARS-S:L (Conners' Adult ADHD Rating Scale - Self-Report, Long form).
  //
  // Source: docs/official-scoring/sources/CAARS.pdf (Conners, Erhardt, Sparrow
  // 1999, MHS). 66 items, 0-3 Likert, raw total range 0-198. The PDF contains
  // NO interpretation bands on raw scores. The MHS manual converts each
  // subscale raw to an age-and-gender-normed T-score (mean 50, SD 10; T>=65
  // designated CAARS-positive / clinically significant) via proprietary
  // tables not reproduced in the source PDF.
  //
  // Subscales reported here (see scoring.js calculateCAARSScore docstring
  // for provenance): A-D empirical factors, E/F DSM-IV symptom counts,
  // G DSM-IV Total (E union F, disjoint), H ADHD Index. E and F are PRIMARY
  // content-verifiable against DSM-IV criteria; A-D and H are SECONDARY
  // from the Conners 1999 MHS manual.
  //
  // This branch:
  //   1. Reports per-subscale RAW scores with structural maxima.
  //   2. Reports the Inconsistency Index (INC) and flags INC>=8 per PDF p. 4.
  //   3. Buckets the overall RAW total into heuristic bands (bandProvenance).
  //   4. Explicitly warns that T-score conversion is not performed.
  if (testType === 'CAARS' && typeof score === 'object' && score.rawScores) {
    let band = config.ranges[0];
    for (const r of config.ranges) {
      if (score.total >= r.min && score.total <= r.max) { band = r; break; }
    }
    if (score.total < config.ranges[0].min) band = config.ranges[0];
    else if (score.total > config.ranges[config.ranges.length - 1].max) band = config.ranges[config.ranges.length - 1];
    const subMax = config.subscaleMax || {};
    const subSummary = Object.entries(score.rawScores)
      .map(([name, val]) => name + ': ' + val + '/' + (subMax[name] ?? '?'))
      .join('; ');
    const incPart = typeof score.inconsistencyIndex === 'number'
      ? ' Inconsistency Index: ' + score.inconsistencyIndex + (score.inconsistencyFlag ? ' (>=8, interpret with caution)' : '') + '.'
      : '';
    return {
      level: band.label,
      desc:
        'Raw total: ' + score.total + '/198. ' +
        'Subscales (raw): ' + subSummary + '.' +
        incPart + ' ' +
        band.description + ' ' +
        'T-score conversion NOT performed: the Conners 1999 MHS manual converts each subscale raw to an age/gender-normed T-score (mean 50, SD 10; T>=65 clinically significant / CAARS-positive) via proprietary tables not reproduced in the source PDF. The overall-total bands shown are heuristic buckets only (config.bandProvenance); clinical interpretation requires T-score conversion against the MHS manual.',
      isBinary: false,
      details: score
    };
  }

  // BPRS (Overall & Gorham, 1962 - 18-item expanded version).
  //
  // The primary source PDF (docs/official-scoring/sources/BPRS.pdf) contains
  // NO interpretation bands. The five severity bands below are SECONDARY,
  // from Leucht et al. (2005) "Linking the PANSS, BPRS, and CGI: clinical
  // implications" (Neuropsychopharmacology 30:2318-2325), which equipercentile-
  // linked BPRS-Expanded totals to CGI-Severity anchors. These bands are
  // declared in src/tests/bprs/config.json and honored here.
  //
  // The BPRS scale is 1-indexed for completed ratings (1 = not present ...
  // 7 = extremely severe). The PDF header also lists "0 = not assessed" as a
  // missing-data marker, NOT a valid completed response. A completed BPRS
  // therefore has every item in [1, 7] and minimum total is 18 (all 1), not
  // 0. Maximum is 126 (18 items x 7).
  if (testType === 'BPRS' && typeof score === 'number') {
    let band = config.ranges[0];
    for (const r of config.ranges) {
      if (score >= r.min && score <= r.max) { band = r; break; }
    }
    // Clamp out-of-range to nearest endpoint rather than silently defaulting.
    if (score < config.ranges[0].min) band = config.ranges[0];
    else if (score > config.ranges[config.ranges.length - 1].max) band = config.ranges[config.ranges.length - 1];
    return {
      level: band.label,
      desc: 'Total: ' + score + '/126 (min 18). ' + band.description + '. Bands: 18-31 Not ill, 32-40 Mildly ill, 41-52 Moderately ill, 53-71 Markedly ill, 72-126 Severely ill. Bands are secondary (Leucht et al. 2005 CGI-linking); the primary BPRS source defines no cut-offs.',
      isBinary: false
    };
  }

  // CPRS-R:S (Conners' Parent Rating Scale - Revised, Short form).
  //
  // Source: docs/official-scoring/sources/CPRS.pdf (Conners 1997, MHS). The
  // PDF contains NO interpretation bands and NO T-score norms. The MHS
  // manual converts each subscale raw score to a gender-and-age-normed
  // T-score (mean 50, SD 10) and flags T>=65 as clinically significant;
  // those norm tables are proprietary and NOT reproduced in the source PDF.
  //
  // This branch therefore:
  //   1. Reports per-subscale RAW scores with structural maxima.
  //   2. Buckets the overall RAW total into the config.ranges bands, which
  //      are declared as HEURISTIC in config.bandProvenance.
  //   3. Explicitly warns in the desc string that T-score conversion is not
  //      performed and that raw cut-offs cannot substitute for T-scores.
  if (testType === 'CPRS' && typeof score === 'object' && score.rawScores) {
    let band = config.ranges[0];
    for (const r of config.ranges) {
      if (score.total >= r.min && score.total <= r.max) { band = r; break; }
    }
    if (score.total < config.ranges[0].min) band = config.ranges[0];
    else if (score.total > config.ranges[config.ranges.length - 1].max) band = config.ranges[config.ranges.length - 1];
    const subMax = config.subscaleMax || {};
    const subSummary = Object.entries(score.rawScores)
      .map(([name, val]) => name + ': ' + val + '/' + (subMax[name] ?? '?'))
      .join('; ');
    return {
      level: band.label,
      desc:
        'Raw total: ' + score.total + '/81. ' +
        'Subscales (raw): ' + subSummary + '. ' +
        band.description + '. ' +
        'T-score conversion NOT performed: the Conners 1997 MHS manual converts each subscale raw to a gender/age-normed T-score (mean 50, SD 10; T>=65 clinically significant, T 61-64 borderline) via proprietary tables not reproduced in the source PDF. The overall-total bands shown are heuristic buckets only (config.bandProvenance); clinical interpretation requires T-score conversion against the MHS manual.',
      isBinary: false,
      details: score
    };
  }

  // CTRS-R:L (Conners' Teacher Rating Scale - Revised, Long form).
  //
  // Source: docs/official-scoring/sources/CTRS.pdf (Conners 1997 MHS, form
  // CRS927). 59 items, 0-3 Likert, raw total range 0-177. The PDF contains
  // NO interpretation bands and NO T-score norms. The MHS manual converts
  // each subscale raw to a gender-and-age-normed T-score (mean 50, SD 10;
  // T>=65 clinically significant, T 61-64 borderline) via proprietary
  // tables not reproduced in the source PDF.
  //
  // Subscales reported here (see scoring.js calculateCTRSScore docstring
  // for provenance):
  //   CGI: Conners' Global Index (Teacher)   10 items  max 30  (SECONDARY)
  //   DSM-IV: Inattentive                     9 items  max 27  (PRIMARY)
  //   DSM-IV: Hyperactive-Impulsive           9 items  max 27  (PRIMARY)
  //   DSM-IV: ADHD Symptoms Total            18 items  max 54  (PRIMARY)
  //
  // Subscales OVERLAP (e.g. item 44 is in both CGI-T and DSM-IV H/I), so
  // the sum of subscale raws does NOT equal the total. This is by design
  // and matches the Conners manual.
  //
  // This branch therefore:
  //   1. Reports per-subscale RAW scores with structural maxima.
  //   2. Buckets the overall RAW total into the config.ranges bands, which
  //      are declared as HEURISTIC in config.bandProvenance.
  //   3. Explicitly warns in the desc string that T-score conversion is
  //      not performed and that raw cut-offs cannot substitute for T-scores.
  if (testType === 'CTRS' && typeof score === 'object' && score.rawScores) {
    let band = config.ranges[0];
    for (const r of config.ranges) {
      if (score.total >= r.min && score.total <= r.max) { band = r; break; }
    }
    if (score.total < config.ranges[0].min) band = config.ranges[0];
    else if (score.total > config.ranges[config.ranges.length - 1].max) band = config.ranges[config.ranges.length - 1];
    const subMax = config.subscaleMax || {};
    const subSummary = Object.entries(score.rawScores)
      .map(([name, val]) => name + ': ' + val + '/' + (subMax[name] ?? '?'))
      .join('; ');
    return {
      level: band.label,
      desc:
        'Raw total: ' + score.total + '/177. ' +
        'Subscales (raw): ' + subSummary + '. ' +
        band.description + '. ' +
        'T-score conversion NOT performed: the Conners 1997 MHS manual converts each subscale raw to a gender/age-normed T-score (mean 50, SD 10; T>=65 clinically significant, T 61-64 borderline) via proprietary tables not reproduced in the source PDF. The overall-total bands shown are heuristic buckets only (config.bandProvenance); clinical interpretation requires T-score conversion against the MHS manual.',
      isBinary: false,
      details: score
    };
  }

  // Standard range-based analysis for all other tests
  // (BDI, BAI, YBOCS, BSDS, IAT, YMRS)
  if (typeof score === 'number') {
    for (let range of config.ranges) {
      if (score >= range.min && score <= range.max) {
        return {
          level: range.label,
          desc: range.description,
          isBinary: false
        };
      }
    }
    // Out-of-range: pick nearest endpoint range rather than blindly returning last.
    // Below-min scores go to the lowest range, above-max to the highest.
    const firstRange = config.ranges[0];
    const lastRange = config.ranges[config.ranges.length - 1];
    const nearest = score < firstRange.min ? firstRange : lastRange;
    return {
      level: nearest.label,
      desc: nearest.description,
      isBinary: false
    };
  }

  // Non-numeric score with no matching handler: default to last range.
  const lastRange = config.ranges[config.ranges.length - 1];
  return {
    level: lastRange.label,
    desc: lastRange.description,
    isBinary: false
  };
};
